import React, { useCallback, useEffect, useState } from "react";
import { stressAdmissionAPI } from "../../services/api/stressAdmissionConfig";

// Bounds mirror the backend StressAdmissionConfig validations.
const BOUNDS = {
  safe_calls_per_worker: { min: 1, max: 20, step: 1, integer: true },
  load_per_core_threshold: { min: 0.5, max: 3.0, step: 0.05, integer: false },
};

export default function AdminStressAdmission() {
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (text) setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await stressAdmissionAPI.get();
      // Rails serializes the decimal column as a JSON string ("1.2"), so coerce
      // both fields to numbers or the numeric validation below rejects them.
      setConfig({
        safe_calls_per_worker: Number(data.config.safe_calls_per_worker),
        load_per_core_threshold: Number(data.config.load_per_core_threshold),
      });
      setDefaults(data.defaults);
    } catch (e) {
      showMsg("error", "Failed to load stress admission config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key, value) => setConfig((c) => ({ ...c, [key]: value }));

  const handleSave = async () => {
    const scpw = config.safe_calls_per_worker;
    if (!Number.isInteger(scpw) || scpw < 1 || scpw > 20) {
      showMsg("error", "Safe calls per worker must be an integer between 1 and 20");
      return;
    }
    const loadThresh = Number(config.load_per_core_threshold);
    if (!Number.isFinite(loadThresh) || loadThresh <= 0.5 || loadThresh > 3.0) {
      showMsg("error", "Load-per-core threshold must be a number between 0.5 and 3.0");
      return;
    }

    setSaving(true);
    try {
      const data = await stressAdmissionAPI.update({
        safe_calls_per_worker: scpw,
        load_per_core_threshold: loadThresh,
      });
      setConfig({
        safe_calls_per_worker: Number(data.config.safe_calls_per_worker),
        load_per_core_threshold: Number(data.config.load_per_core_threshold),
      });
      showMsg("success", "Stress admission config saved");
    } catch (e) {
      const errs = e.response?.data?.errors;
      showMsg("error", (errs && errs.join(", ")) || "Failed to save stress admission config");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading stress admission config…</div>;
  }

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-foreground">Stress Admission</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Per-task call packing for the stress-test caller fleet. Applies to{" "}
          <strong>provisioned fleet servers only</strong> — live calls and small on-box tests
          use the standard limit. Changes apply to the next fleet launch — no deploy, no restart.
        </p>
      </div>

      {msg.text && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            msg.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-4 space-y-4 max-w-md">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Safe calls per worker
          </label>
          <input
            type="number"
            min={BOUNDS.safe_calls_per_worker.min}
            max={BOUNDS.safe_calls_per_worker.max}
            step={BOUNDS.safe_calls_per_worker.step}
            value={config.safe_calls_per_worker}
            onChange={(e) => set("safe_calls_per_worker", parseInt(e.target.value, 10))}
            className="w-24 rounded border border-input px-2 py-1 text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Calls admitted per uvicorn worker on a fleet task (max calls per task ={" "}
            <code>UVICORN_WORKERS × this</code>). Higher packs more calls per server.
            {defaults ? ` Default ${defaults.safe_calls_per_worker}.` : ""}
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Load-per-core threshold
          </label>
          <input
            type="number"
            min={BOUNDS.load_per_core_threshold.min}
            max={BOUNDS.load_per_core_threshold.max}
            step={BOUNDS.load_per_core_threshold.step}
            value={config.load_per_core_threshold}
            onChange={(e) => set("load_per_core_threshold", parseFloat(e.target.value))}
            className="w-24 rounded border border-input px-2 py-1 text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            A fleet task stops accepting calls once its 1-minute load average per core reaches
            this. Raise to let a task run hotter before shedding.
            {defaults ? ` Default ${defaults.load_per_core_threshold}.` : ""}
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
